import type { ContentType } from '@pigello/construct-core';
import type { ModelName } from '@pigello/pigello-matrix';

export function getModelName(contentType: ContentType): ModelName {
  return contentType.split('.').pop() as ModelName;
}

export function isVisibleField(
  meta: { isReadonly?: boolean },
  fieldPerms: { canView?: boolean } | undefined
) {
  if (meta.isReadonly) {
    return false;
  }
  if (fieldPerms && fieldPerms.canView === false) {
    return false;
  }
  return true;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isMessageScalar(value: unknown) {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  );
}

export function stringifyMessage(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (
    value === null ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  if (isRecord(value) && typeof value.message === 'string') {
    return value.message;
  }
  return undefined;
}

export function toFieldError(error: unknown): { message: string } | undefined {
  const message = stringifyMessage(error);
  return message ? { message } : undefined;
}

/** The slice of TanStack's field meta that error collection reads. */
interface CollectFieldErrorsArgs {
  errorMap: {
    onMount?: unknown;
    onChange?: unknown;
    onBlur?: unknown;
    onSubmit?: unknown;
    onServer?: unknown;
  };
  isTouched: boolean;
}

/**
 * Client validation can fire on UNTOUCHED fields via `onChangeListenTo`
 * cross-field validators — hide those until the field is touched (submitting
 * touches every field, so submit errors always show). Server-injected errors
 * (`onServer`) always show: they only exist after a deliberate injection.
 */
export function collectFieldErrors({
  errorMap,
  isTouched,
}: CollectFieldErrorsArgs): Array<{ message: string }> {
  return [
    ...(isTouched
      ? [
          errorMap.onMount,
          errorMap.onChange,
          errorMap.onBlur,
          errorMap.onSubmit,
        ]
      : []),
    errorMap.onServer,
  ]
    .flat()
    .map(toFieldError)
    .filter((error): error is { message: string } => Boolean(error));
}
